import { createSelector } from "@reduxjs/toolkit";
import { diff } from "deep-object-diff";

import {
  BILLING_WEIGHTED_MIN_WASTE_PER_HOUSEHOLD_MAX,
  BILLING_WEIGHTED_MIN_WASTE_PER_HOUSEHOLD_OFFSET,
} from "../consts/billing";
import { RootState } from "../store";
import { BILLING_STEPS, BillingStep } from "../types/billing";
import { BillingStatus } from "../types/billings";
import { calculateBillingExpensePerKg } from "../utils/billing";

export const getBillingState = (state: RootState) => state.billing;
export const getBillingErrorMsg = (state: RootState) => state.billing.errorMsg;
export const getBillingIsUpdating = (state: RootState) => state.billing.isUpdating;
export const getBillingBilling = (state: RootState) => state.billing.billing;
export const getBillingOriginalBilling = (state: RootState) => state.billing.originalBilling;
export const getBillingId = (state: RootState) => state.billing.billing.billingId;
export const getBillingCurrentStep = (state: RootState) => state.billing.currentStep;
export const getBillingIsTouched = createSelector(
  [getBillingBilling, getBillingOriginalBilling],
  (billing, originalBilling) => {
    const dataDiff = diff(billing, originalBilling);
    return Object.keys(dataDiff).length > 0;
  }
);
export const getBillingExpenses = (state: RootState) => state.billing.billing.expenses;
export const getBillingExpectedWasteAmount = (state: RootState) => state.billing.billing.expectedWasteAmount;
export const getBillingRealWasteAmount = (state: RootState) => state.billing.billing.wasteAmount;
export const getBillingHouseholdWithFixedOnlyCount = (state: RootState) =>
  state.billing.billing.householdWithFixedOnlyCount;
export const getIsBillingInNewStatus = (state: RootState): boolean =>
  state.billing.billing.status === BillingStatus.New;
export const getIsBillingInActiveStatus = (state: RootState): boolean =>
  state.billing.billing.status === BillingStatus.Active;

export const getBillingExpectedExpenses = createSelector([getBillingExpenses], (expenses) =>
  expenses.filter((expense) => !expense.real)
);
export const getBillingRealExpenses = createSelector([getBillingExpenses], (expenses) =>
  expenses.filter((expense) => expense.real)
);

export const getBillingExpectedExpensesValueSum = createSelector([getBillingExpectedExpenses], (expenses) =>
  expenses.reduce((sum, expense) => sum + Number(expense.value), 0)
);
export const getBillingRealExpensesValueSum = createSelector([getBillingRealExpenses], (expenses) =>
  expenses.reduce((sum, expense) => sum + Number(expense.value), 0)
);

export const getBillingOriginalExpectedExpensesValueSum = createSelector([getBillingOriginalBilling], (billing) =>
  billing.expenses.filter((expense) => !expense.real).reduce((sum, expense) => sum + Number(expense.value), 0)
);

export const getBillingExpectedExpensePerKg = createSelector(
  [getBillingExpectedWasteAmount, getBillingExpectedExpenses],
  (expectedWasteAmount, expectedExpenses) => {
    return calculateBillingExpensePerKg(expectedExpenses, Number(expectedWasteAmount));
  }
);
export const getBillingRealExpensePerKg = createSelector(
  [getBillingRealWasteAmount, getBillingRealExpenses],
  (realWasteAmount, realExpenses) => {
    return calculateBillingExpensePerKg(realExpenses, Number(realWasteAmount));
  }
);

export const getBillingWeighted = (state: RootState) => state.billing.billing.weighted;
export const getBillingLocationIncreaseComparison = (state: RootState) =>
  state.billing.billing.locationIncreaseComparison;
export const getBillingLocationIncreaseComparisonPositiveSum = createSelector(
  [getBillingLocationIncreaseComparison],
  (comparison) =>
    comparison.positive0To5 +
    comparison.positive5To10 +
    comparison.positive10To15 +
    comparison.positive15To20 +
    comparison.positive20To25 +
    comparison.positive25
);
export const getBillingLocationIncreaseComparisonNegativeSum = createSelector(
  [getBillingLocationIncreaseComparison],
  (comparison) =>
    comparison.negative0To5 +
    comparison.negative5To10 +
    comparison.negative10To15 +
    comparison.negative15To20 +
    comparison.negative20To25 +
    comparison.negative25
);

export const getBillingPaymentSchedule = (state: RootState) => state.billing.billing.paymentSchedule;
export const getBillingPaymentScheduleType = (state: RootState) => state.billing.billing.paymentSchedule.type;

export const getCanSaveBillingCosts = createSelector(
  [getBillingExpectedWasteAmount, getBillingExpectedExpensesValueSum, getBillingExpectedExpenses],
  (expectedWasteAmount, expectedExpensesValueSum, expectedExpenses) =>
    Number(expectedWasteAmount) > 0 &&
    expectedExpensesValueSum > 0 &&
    expectedExpenses.every((expense) => expense.name.trim() !== "")
);

export const getCanSaveBillingBilling = createSelector(
  [getBillingRealWasteAmount, getBillingRealExpensesValueSum, getBillingRealExpenses],
  (realWasteAmount, realExpensesValueSum, realExpenses) =>
    Number(realWasteAmount) > 0 &&
    realExpensesValueSum > 0 &&
    realExpenses.every((expense) => expense.name.trim() !== "")
);

export const getBillingAvailableSteps = createSelector(
  [getBillingOriginalBilling, getBillingOriginalExpectedExpensesValueSum],
  (billing, expectedExpensesValueSum) => {
    if (billing.status === BillingStatus.Active || billing.status === BillingStatus.Closed) {
      return BILLING_STEPS;
    }

    if (expectedExpensesValueSum > 0) {
      return [BillingStep.Costs, BillingStep.Calculation];
    }

    return [BillingStep.Costs];
  }
);

export const getBillingCollectedFeePercentage = createSelector([getBillingBilling], (billing) => {
  if (billing.totalNetFeeSum === 0) {
    return 0;
  }

  return (billing.totalCollectedFeeSum / billing.totalNetFeeSum) * 100;
});

export const getBillingShouldDisplayMinWastePerHouseholdField = createSelector([getBillingWeighted], (weighted) => {
  return weighted.lastYearsWasteSum === 0;
});

const calculateMinWastePerHousehold = (expectedWasteAmount: number, householdCount: number, offset = 0) => {
  if (householdCount === 0) {
    return BILLING_WEIGHTED_MIN_WASTE_PER_HOUSEHOLD_MAX;
  }
  return Math.round(expectedWasteAmount / householdCount) + offset;
};

export const getBillingWeightedMinWastePerHouseholdSuggested = createSelector([getBillingBilling], (billing) =>
  calculateMinWastePerHousehold(Number(billing.expectedWasteAmount), billing.householdCount)
);

export const getBillingWeightedMinWastePerHouseholdMax = createSelector([getBillingBilling], (billing) =>
  calculateMinWastePerHousehold(
    Number(billing.expectedWasteAmount),
    billing.householdCount,
    BILLING_WEIGHTED_MIN_WASTE_PER_HOUSEHOLD_OFFSET
  )
);
